import { Box } from "@mui/material";
import { AppModal } from "global/components/AppModal";
import strings from "global/constants/StringConstants";
import { fillCopy } from "helpers/methods";
import styles from "../AiWebForm.styles";
import tokens from "../AiWebForm.tokens";
import { AiFieldMapEntry, AiWarning } from "../AiWebFormTypesAndValidation";
import { AlertGlyph, GlobeGlyph, InfoGlyph, TextGlyph } from "./AiWebFormIcons";

const copy = strings.aiWebForm;

interface Props {
  open: boolean;
  version: number | null;
  /** The signer has another active web form, which this one replaces. */
  replaces: boolean;
  pageTexts: string[];
  labelWarnings: AiWarning[];
  fieldMap?: Record<string, AiFieldMapEntry>;
  /** The checks are still being read. */
  loading: boolean;
  publishing: boolean;
  onClose: () => void;
  onPublish: () => void;
}

const fieldTitle = (warning: AiWarning, fieldMap?: Record<string, AiFieldMapEntry>) => {
  const field = warning.key ? fieldMap?.[warning.key] : undefined;
  return field?.dataLabel || field?.name || warning.key || "";
};

/** "Publish version 3?": what signers will newly read, and the fields whose values would not reach the PDF. */
export const AiPublishModal = ({
  open,
  version,
  replaces,
  pageTexts,
  labelWarnings,
  fieldMap,
  loading,
  publishing,
  onClose,
  onPublish,
}: Props) => (
  <AppModal
    open={open}
    onClose={onClose}
    title={version != null ? fillCopy(copy.publishTitle, { version }) : copy.publish}
    subtitle={copy.publishSubtitle}
    icon={<GlobeGlyph />}
    width={tokens.layout.publishWidth}
    disableSheet
    busy={publishing}
    primaryLabel={
      <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: "8px", "& svg": { width: 16, height: 16 } }}>
        <GlobeGlyph />
        {version != null ? fillCopy(copy.publishVersion, { version }) : copy.publish}
      </Box>
    }
    onPrimary={onPublish}
    primaryDisabled={loading || version == null}
    primaryLoading={publishing}
    cancelLabel={copy.backToEditing}
  >
    <Box sx={styles.dialogBody}>
      {replaces && (
        <Box sx={styles.replaceNotice}>
          <InfoGlyph />
          <span>
            <strong>{copy.replaceNoticeStrong}</strong>
            {copy.replaceNoticeRest}
          </span>
        </Box>
      )}
      {pageTexts.length > 0 && (
        <>
          <Box sx={styles.sectionHeading}>
            <TextGlyph />
            {copy.newTextHeading}
          </Box>
          <Box sx={styles.sectionBody}>{copy.newTextBody}</Box>
          <Box sx={{ display: "flex", flexDirection: "column", gap: "6px" }}>
            {pageTexts.map((text) => (
              <Box key={text} sx={styles.pageText}>
                {text}
              </Box>
            ))}
          </Box>
        </>
      )}
      {labelWarnings.length > 0 && (
        <>
          <Box sx={styles.sectionHeading}>
            <AlertGlyph />
            {copy.checkFieldsHeading}
          </Box>
          {labelWarnings.map((warning) => (
            <Box key={`${warning.key}-${warning.code}`} sx={styles.fieldWarning}>
              <AlertGlyph />
              <Box sx={{ minWidth: 0 }}>
                <Box sx={styles.fieldWarningTitle}>{fieldTitle(warning, fieldMap)}</Box>
                <Box sx={{ pt: "2px" }}>{warning.message}</Box>
              </Box>
            </Box>
          ))}
        </>
      )}
    </Box>
  </AppModal>
);
